import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FocusTrap from 'focus-trap-react';
import { useEffectOnce, useEventListener } from 'usehooks-ts';
import classNames from 'classnames';

import { Link } from '../../link/link';
import Svg from '../../svg/svg.component';
import { TitlePropsV2 } from '../../titlev2/titlev2.component';
import TypographyV2 from '../../typographyv2/typographyv2';
import { Keycodes } from '../../../utils/keyboard';
import { useOnClickOutside } from '../../../utils/use-on-click-outside';
import { useScrollBlock } from '../../../utils/use-scroll-block';

import './live-demo-card-modal.scss';

export interface LiveDemoCardModalProps {
  onClose: () => void;
  children?: ReactNode;
  externalizedLink?: string;
  title?: TitlePropsV2;
}

export const MODAL_ANIMATION_TIME = 300;

const LiveDemoCardModal = ({ children, onClose, externalizedLink, title }: LiveDemoCardModalProps) => {
  const [visibility, setVisibility] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const [blockScroll, allowScroll] = useScrollBlock();

  const encodedTitle = useMemo(() => (title ? encodeURIComponent(title.lines.join(' ')) : ''), [title]);
  const encodedUrl = useMemo(() => (externalizedLink ? encodeURIComponent(externalizedLink) : ''), [externalizedLink]);

  const onModalClose = () => {
    setVisibility(false);
    setTimeout(onClose, MODAL_ANIMATION_TIME);
  };

  const closeOnEscape = (e: KeyboardEvent) => {
    if (e.key === Keycodes.Escape) {
      onModalClose();
    }
  };

  useEventListener('keydown', closeOnEscape);
  useOnClickOutside(ref, onModalClose);

  useEffectOnce(() => {
    setVisibility(true);
  });

  useEffect(() => {
    blockScroll();

    return () => {
      allowScroll();
    };
  }, [allowScroll, blockScroll]);

  return (
    <div
      className={classNames('snowflake-live-demo-card-modal-overlay', {
        'snowflake-live-demo-card-modal-overlay-visible': visibility,
      })}
      data-testid="snowflake-live-demo-card-modal-overlay"
    >
      <FocusTrap focusTrapOptions={{ fallbackFocus: 'body' }}>
        <div ref={ref} className="snowflake-live-demo-card-modal" aria-modal="true" role="dialog">
          <div className="snowflake-live-demo-card-modal-header">
            <TypographyV2 className="snowflake-live-demo-card-modal-header-title" variant="heading4">
              {t('share_event')}
            </TypographyV2>
          </div>
          <div className="snowflake-live-demo-card-modal-inner">
            <div className="snowflake-live-demo-card-modal-content">{children}</div>
            {externalizedLink && title ? (
              <div
                className="snowflake-live-demo-card-modal-footer"
                data-testid="snowflake-live-demo-card-modal-footer"
              >
                <Link
                  link={{
                    url: `https://x.com/intent/post?url=${encodedUrl}&text=${encodedTitle}`,
                    attributes: { target: '_blank' },
                  }}
                  className="snowflake-live-demo-card-modal-footer-link"
                >
                  <Svg className="snowflake-live-demo-card-modal-footer-icon" name="twitter-icon" />
                  <TypographyV2 variant="body3">{t('twitter')}</TypographyV2>
                </Link>
                <Link
                  link={{
                    url: `mailto:?subject=${encodedTitle}&body=${encodedUrl}`,
                  }}
                  className="snowflake-live-demo-card-modal-footer-link"
                >
                  <Svg className="snowflake-live-demo-card-modal-footer-icon" name="mail-solid" />
                  <TypographyV2 variant="body3">{t('email')}</TypographyV2>
                </Link>
                <Link
                  link={{
                    url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
                    attributes: { target: '_blank' },
                  }}
                  className="snowflake-live-demo-card-modal-footer-link"
                >
                  <Svg className="snowflake-live-demo-card-modal-footer-icon" name="facebook-icon" />
                  <TypographyV2 variant="body3">{t('facebook')}</TypographyV2>
                </Link>
                <Link
                  link={{
                    url: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}`,
                    attributes: { target: '_blank' },
                  }}
                  className="snowflake-live-demo-card-modal-footer-link"
                >
                  <Svg className="snowflake-live-demo-card-modal-footer-icon" name="linkedin-icon" />
                  <TypographyV2 variant="body3">{t('linkedin')}</TypographyV2>
                </Link>
              </div>
            ) : null}
          </div>
        </div>
      </FocusTrap>
    </div>
  );
};

export default LiveDemoCardModal;
